import ContactPerson from '../types/ContactPerson';
import client from '../../api/client';
import BookingOrder from '../types/BookingOrder';
import ApiResponse from '../../types/ApiResponse';
import PaginationMeta from '../../types/PaginationMeta';
import PaginationParams from '../../types/PaginationParams';

export const DEFAULT_CONTACT_PERSON_PAGE_SIZE = 25;

export type ContactPersonFilterParams = {
  ids?: ContactPerson['id'][];
  query?: string;
  name?: string;
  email?: string;
};

export type ContactPersonsResponse =
  | ContactPerson[]
  | ApiResponse<ContactPerson[], PaginationMeta>;

type CreateContactPersonParams = {
  firstName: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  note?: string;
};

type UpdateContactPersonParams = {
  firstName: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  note?: string;
};

export const normalizeContactPersonsResponse = (
  response: ContactPersonsResponse,
  fallbackPagination: Required<PaginationParams>,
) => {
  if (Array.isArray(response)) {
    return {
      contactPersons: response,
      isPaginated: false,
      meta: {
        page: fallbackPagination.page,
        pageSize: fallbackPagination.pageSize,
        lastPage: fallbackPagination.page,
        total: response.length,
      },
    };
  }

  return {
    contactPersons: response.data,
    isPaginated: true,
    meta: response.meta,
  };
};

const getContactPersons = (
  filterParams?: ContactPersonFilterParams,
  paginationParams?: PaginationParams | null,
) =>
  client.get<ContactPersonsResponse>(`/contact-persons/`, {
    params: {
      ...filterParams,
      ...paginationParams,
    },
  });

const getContactPerson = (id: ContactPerson['id']) =>
  client.get<ContactPerson>(`/contact-persons/${id}`);

const createContactPerson = (contactPerson: CreateContactPersonParams) =>
  client.post<ContactPerson>('/contact-persons', contactPerson);

const updateContactPerson = (
  id: ContactPerson['id'],
  contactPerson: UpdateContactPersonParams,
) => client.patch<ContactPerson>(`/contact-persons/${id}`, contactPerson);

const deleteContactPerson = (id: ContactPerson['id']) =>
  client.delete<ContactPerson>(`/contact-persons/${id}`);

const deleteBookingOrderContactPerson = (
  contactPersonId: ContactPerson['id'],
  editedBookingOrderId: BookingOrder['id'] | null,
  replacementContactPersonId: ContactPerson['id'],
) =>
  client.delete<ContactPerson>(
    `/booking-order/${editedBookingOrderId}/contact-person/${contactPersonId}/replacement/${replacementContactPersonId}`,
  );
const deleteBookingOrderContactPersonDryRun = (
  contactPersonId: ContactPerson['id'],
  editedBookingOrderId: BookingOrder['id'] | null,
) =>
  client.delete<ContactPerson>(
    `/booking-order/${editedBookingOrderId}/contact-person/${contactPersonId}/dry-run`,
  );

export default {
  getContactPersons,
  getContactPerson,
  createContactPerson,
  updateContactPerson,
  deleteContactPerson,
  deleteBookingOrderContactPerson,
  deleteBookingOrderContactPersonDryRun,
};
